// src/hooks/useIdleStatus.ts
// Track user idle state for presence auto-away

import { useState, useEffect, useRef, useCallback } from 'react';

// Idle timeout: 5 minutes of inactivity
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;

// Throttle activity updates to avoid heavy processing
const ACTIVITY_THROTTLE_MS = 500;

export interface UseIdleStatusReturn {
  /** Whether the user is currently idle (no activity for 5+ minutes while tab is visible) */
  isIdle: boolean;
  /** Timestamp of last user activity */
  lastActiveAt: number;
  /** Manually reset idle state (e.g., for testing) */
  resetIdle: () => void;
}

/**
 * Hook to track user idle state.
 * 
 * - Tracks mousemove, keydown, touchstart, scroll
 * - If tab is visible AND no activity for 5 minutes -> isIdle = true
 * - On any activity -> isIdle = false
 * - Tab hidden doesn't count as idle (separate visibility handling)
 */
export function useIdleStatus(): UseIdleStatusReturn {
  const [isIdle, setIsIdle] = useState(false);
  const [lastActiveAt, setLastActiveAt] = useState(() => Date.now());
  
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const throttleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityTimeRef = useRef<number>(Date.now());

  // Clear idle timeout
  const clearIdleTimeout = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
  }, []);

  // Start idle timer
  const startIdleTimer = useCallback(() => {
    clearIdleTimeout();
    
    // Only start idle timer if tab is visible
    if (document.visibilityState !== 'visible') {
      return;
    }
    
    idleTimeoutRef.current = setTimeout(() => {
      setIsIdle(true);
    }, IDLE_TIMEOUT_MS);
  }, [clearIdleTimeout]);

  // Handle user activity (throttled)
  const handleActivity = useCallback(() => {
    // Skip if we're still in throttle window
    if (throttleTimeoutRef.current) {
      return;
    }
    
    // Throttle subsequent calls
    throttleTimeoutRef.current = setTimeout(() => {
      throttleTimeoutRef.current = null;
    }, ACTIVITY_THROTTLE_MS);
    
    // Update last active time
    const now = Date.now();
    lastActivityTimeRef.current = now;
    setLastActiveAt(now);
    
    // Reset idle state
    if (isIdle) {
      setIsIdle(false);
    }
    
    // Restart idle timer
    startIdleTimer();
  }, [isIdle, startIdleTimer]);

  // Handle visibility change
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      // Tab became visible - check if we should be idle
      const now = Date.now();
      const timeSinceActivity = now - lastActivityTimeRef.current;
      
      if (timeSinceActivity >= IDLE_TIMEOUT_MS) {
        setIsIdle(true);
      } else {
        // Start timer for remaining time
        clearIdleTimeout();
        idleTimeoutRef.current = setTimeout(() => {
          setIsIdle(true);
        }, IDLE_TIMEOUT_MS - timeSinceActivity);
      }
    } else {
      // Tab hidden - clear idle timer (visibility-based away handled separately)
      clearIdleTimeout();
    }
  }, [clearIdleTimeout]);

  // Manual reset (for testing)
  const resetIdle = useCallback(() => {
    const now = Date.now();
    lastActivityTimeRef.current = now;
    setLastActiveAt(now);
    setIsIdle(false);
    startIdleTimer();
  }, [startIdleTimer]);

  // Setup event listeners
  useEffect(() => {
    const events = ['mousemove', 'keydown', 'touchstart', 'scroll', 'mousedown', 'click'];
    
    // Add listeners with passive option for performance
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Start initial idle timer
    startIdleTimer();
    
    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      clearIdleTimeout();
      
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = null;
      }
    };
  }, [handleActivity, handleVisibilityChange, startIdleTimer, clearIdleTimeout]);

  return {
    isIdle,
    lastActiveAt,
    resetIdle,
  };
}

export default useIdleStatus;
